import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import storageService from '../../../utils/storageService';
import CheckpointMasterList from './components/CheckpointMasterList';
import ChecklistTemplateBuilder from './components/ChecklistTemplateBuilder';
import RoundSchedulerList from './components/RoundSchedulerList';
import LiveOperationsMonitor from './components/LiveOperationsMonitor';
import PageHeadingSection from '../pageheading/PageHeadingSection';
import Breadcrumbs from '../BreadCrum/BreadCrum';

const ALL_TABS = [
  { id: 'CHECKPOINTS', path: 'locations', label: 'Checkpoints & QR Tags' },
  { id: 'TEMPLATES', path: 'templates', label: 'Checklist Templates' },
  { id: 'SCHEDULES', path: 'rounds', label: 'Patrol Schedules' },
  { id: 'LIVE_MONITOR', path: 'monitoring', label: 'Live Operations Stream' },
];

const RoundInspectionModule = () => {
  const { user: authUser } = useSelector((state) => state.auth || {});
  const location = useLocation();
  const navigate = useNavigate();

  // Robust Vendor detection with storageService decrypt fallback
  const userRoleId = Number(storageService.getItem('roleId') || authUser?.roleId || 0);
  const personTypeId = Number(storageService.getItem('personTypeId') || authUser?.personTypeId || 0);
  const isVendorLogin = userRoleId === 7 || personTypeId === 4;

  const visibleTabs = isVendorLogin
    ? [
        { id: 'SCHEDULES', path: 'rounds', label: 'My Assigned Patrol Rounds' },
        { id: 'LIVE_MONITOR', path: 'monitoring', label: 'Live Operations Stream' },
      ]
    : ALL_TABS;

  // Helper to determine tab from current URL pathname
  const getTabFromPath = (pathname) => {
    if (!pathname) return isVendorLogin ? 'SCHEDULES' : 'CHECKPOINTS';
    if (pathname.includes('/templates')) return isVendorLogin ? 'SCHEDULES' : 'TEMPLATES';
    if (pathname.includes('/rounds') || pathname.includes('/assignments')) return 'SCHEDULES';
    if (pathname.includes('/monitoring') || pathname.includes('/reports') || pathname.includes('/live')) return 'LIVE_MONITOR';
    if (pathname.includes('/locations')) return isVendorLogin ? 'SCHEDULES' : 'CHECKPOINTS';
    return isVendorLogin ? 'SCHEDULES' : 'CHECKPOINTS';
  };

  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Sync tab state when URL changes (e.g. from Sidebar navigation)
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname, isVendorLogin]);

  // Dynamic siteId from logged-in user or local storage
  const storedSiteId =
    authUser?.siteId ||
    authUser?.SiteId ||
    storageService.getItem('siteId') ||
    2;

  // Handle tab click with URL navigation
  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    const currentPath = location.pathname;
    const match = currentPath.match(/(.*\/round-inspection)/);
    const basePath = match ? match[1] : '/round-inspection';
    navigate(`${basePath}/${tab.path}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'CHECKPOINTS':
        return isVendorLogin
          ? <RoundSchedulerList siteId={storedSiteId} authUser={authUser} />
          : <CheckpointMasterList siteId={storedSiteId} authUser={authUser} />;
      case 'TEMPLATES':
        return isVendorLogin
          ? <RoundSchedulerList siteId={storedSiteId} authUser={authUser} />
          : <ChecklistTemplateBuilder siteId={storedSiteId} authUser={authUser} />;
      case 'SCHEDULES':
        return <RoundSchedulerList siteId={storedSiteId} authUser={authUser} />;
      case 'LIVE_MONITOR':
        return <LiveOperationsMonitor siteId={storedSiteId} authUser={authUser} />;
      default:
        return isVendorLogin
          ? <RoundSchedulerList siteId={storedSiteId} authUser={authUser} />
          : <CheckpointMasterList siteId={storedSiteId} authUser={authUser} />;
    }
  };

  return (
    <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4">
      {/* Breadcrumb Navigation */}
      <Breadcrumbs items={['Dashboard', 'Security & Operations', isVendorLogin ? 'My Assigned Patrol Rounds' : 'Staff Rounds & Inspection']} />

      {/* Page Heading */}
      <PageHeadingSection
        mainHeading={isVendorLogin ? "My Assigned Patrol Rounds" : "Staff Rounds & Inspection"}
        subHeading={
          isVendorLogin
            ? "View patrol routes assigned to your security agency, assign your guards to daily tours, and monitor real-time patrol execution."
            : "Configure physical QR patrol checkpoints, build dynamic checklist schemas, schedule shift rounds, and monitor live guard patrols."
        }
      />

      {/* Main Container matching AmenitiesModule design */}
      <div className="bg-white dark:bg-cardbg rounded-xl shadow-sm border border-gray-100 dark:border-bordercolor overflow-hidden flex-1 flex flex-col shrink-0">
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 dark:border-bordercolor">
          <nav className="flex -mb-px px-6 flex-wrap" aria-label="Tabs">
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`py-4 px-6 text-center border-b-2 text-xs sm:text-sm transition-all whitespace-nowrap ${
                    isActive
                      ? 'border-indigo-600 text-indigo-600 font-bold'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto flex-1 bg-white dark:bg-cardbg">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default RoundInspectionModule;
